'use client';

import React from 'react';
import Image from 'next/image';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import { twJoin } from 'tailwind-merge';
import { LegalGuaranteeBadge } from '@/ui/components/legal-guarantee-badge/legal-guarantee-badge';
import Link from 'next/link';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { Disclosure } from '@headlessui/react';
import { PlusIcon } from '@heroicons/react/20/solid';
import { usePathname } from 'next/navigation';
import { hideHeaderFooterPaths, paths } from '@/constants/paths';
import { pathMatchIgnoreLocale } from '@/ui/utils/localization-helpers/localization';
import CookieManager from '@/ui/utils/cookie-manager';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { buildTrackingId } from '@/utils/tracking-helper';
import { slugify } from '@/utils/url-helper';
import { LanguageCountryList } from '@/integrations/crystallize/types/localization';
import type { Messages } from 'next-intl';
import ButtonLink from '../../shared/ButtonLink';
import { RichTextRenderer } from '../../tileRenderers/RichTextRenderer';
import OptimizedImage from '../../shared/OptimizedImage';
import NukiLogo from '../../../../../public/images/footer/nuki-logo-white.svg';
import LanguageCountrySelector from '../language-country-selector/language-country-selector';
import { FooterContent } from './footer-utils';
import { getWithdrawalLinkClickHandler } from './is-withdrawal-link';

function getChunksFromFooter(footerData: FooterContent, chunkId: string) {
  const componentContent = footerData?.components?.find((c) => c.id === chunkId)?.content;
  const components =
    componentContent?.__typename === 'ContentChunkContent' ? componentContent.chunks : [];
  return components;
}

function extractLink(link: any) {
  const title = extractComponentFromChunk(
    link,
    'title',
    'SingleLineContent' as never, // idk why never, but breaks otherwise
    // @ts-ignore
  )?.text;

  const internalUrl = extractComponentFromChunk(
    link,
    'internal-link',
    'ItemRelationsContent' as never,
    // @ts-ignore
  )?.items?.[0]?.path;

  const externalUrl = extractComponentFromChunk(
    link,
    'external-link',
    'SingleLineContent' as never,
    // @ts-ignore
  )?.text;

  const url = internalUrl || externalUrl || undefined;

  return {
    title,
    url,
    target: externalUrl ? '_blank' : '_self',
  };
}

interface FooterProps {
  footerData: FooterContent;
  languageCountryList: LanguageCountryList;
  chooserUiLanguage: string;
  chooserUiMessages: Messages;
}

export function Footer({
  footerData,
  languageCountryList,
  chooserUiLanguage,
  chooserUiMessages,
}: Readonly<FooterProps>) {
  const { country } = useLocalization();
  const { localizedLink } = useLocaleLink();
  const advantages = getChunksFromFooter(footerData, 'advantages');
  const newsletter = getChunksFromFooter(footerData, 'newsletter');
  const columnLinks = getChunksFromFooter(footerData, 'column-links');
  const socials = getChunksFromFooter(footerData, 'socials');
  const paymentMethods = getChunksFromFooter(footerData, 'payment-methods');
  const bottomLinks = getChunksFromFooter(footerData, 'bottom-links');

  // The link columns and the row beneath them share one track definition so the legal guarantee
  // badge and the country selector line up with the columns above instead of floating between
  // them. Derived from the CMS column count, so it follows however many columns are authored.
  // It is inert until both containers switch to `display: grid` at xl.
  const footerColumnsStyle = columnLinks?.length
    ? { gridTemplateColumns: `repeat(${columnLinks.length}, minmax(0, 1fr))` }
    : undefined;
  const hasSocials = Boolean(socials?.length);
  const hasPaymentMethods = Boolean(paymentMethods?.length);

  // Hide the footer on checkout page and US landing page, and additional paths
  const currentPath = usePathname();

  // No footer document resolved for this country (and no legacy footer) → render nothing.
  if (!footerData) {
    return null;
  }

  if (
    pathMatchIgnoreLocale(currentPath, paths.checkout) ||
    pathMatchIgnoreLocale(currentPath, paths.usLandingPage) ||
    hideHeaderFooterPaths.some((path) => pathMatchIgnoreLocale(currentPath, path))
  ) {
    return null;
  }

  return (
    <footer className="text-white">
      <div className="bg-neutral-80">
        <div className="mx-auto flex max-w-screen-2xl flex-col gap-4 p-4 sm:p-10 xl:flex-row">
          <Image src={NukiLogo} alt="Nuki" width={150} height={70} className="h-16 w-auto" />
          <div className="mx-auto grid grid-cols-2 gap-4 sm:gap-10 md:flex md:gap-14 lg:mr-14 lg:gap-25">
            {advantages?.map((advantage, i) => {
              const icon = extractComponentFromChunk(advantage, 'icon', 'ImageContent');
              const title = extractComponentFromChunk(
                advantage,
                'title',
                'SingleLineContent',
              )?.text;
              const description = extractComponentFromChunk(
                advantage,
                'description',
                'RichTextContent',
              );
              const isLast = i === advantages.length - 1;
              return (
                <div
                  key={`${title}-${i}`}
                  className={twJoin(
                    'flex flex-col items-center gap-4 text-center md:flex-row md:text-left',
                    isLast && 'col-span-2 mx-auto md:col-span-1',
                  )}
                >
                  <OptimizedImage
                    imageComponent={icon}
                    width={40}
                    height={40}
                    className="h-10 w-auto"
                    sizes="40px"
                  />
                  <RichTextRenderer blocks={description} className="text-lg" />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="bg-neutral-100">
        <div className="mx-auto max-w-screen-2xl p-4 sm:p-10">
          <div className="grid-rows-[auto, auto] grid grid-cols-1 gap-10 sm:gap-14 md:grid-cols-[max-content,1fr]">
            {/* Newsletter */}
            <div className="text-center sm:max-w-xs sm:text-left">
              {newsletter?.map((chunk) => {
                const title = extractComponentFromChunk(chunk, 'title', 'RichTextContent');
                const description = extractComponentFromChunk(
                  chunk,
                  'description',
                  'RichTextContent',
                );
                const cta = extractComponentFromChunk(chunk, 'cta', 'PieceContent');

                return (
                  <div key="footer-newsletter" className="flex flex-col gap-1">
                    <div className="mx-auto max-w-xs">
                      <RichTextRenderer blocks={title} className="text-2xl sm:text-3xl" />
                      <RichTextRenderer blocks={description} className="sm:text-md" />
                    </div>
                    <ButtonLink
                      ctaComponent={cta}
                      className="mt-4"
                      trackingPrefix="footer-newsletter"
                    />
                  </div>
                );
              })}
            </div>
            {/* Column links */}
            {/* Desktop */}
            <div className="hidden flex-wrap gap-16 sm:flex xl:grid" style={footerColumnsStyle}>
              {columnLinks?.map((column, i) => {
                const title = extractComponentFromChunk(column, 'title', 'SingleLineContent')?.text;
                const linksPieceContent = extractComponentFromChunk(
                  column,
                  'footer-column-link',
                  'PieceContent',
                )?.components?.[0]?.content;
                const links =
                  linksPieceContent?.__typename === 'ContentChunkContent'
                    ? linksPieceContent.chunks
                    : [];

                return (
                  <ul key={slugify(`${title}-${i}`)} className="flex flex-col gap-2">
                    <h4 className="mb-4 text-lg font-medium">{title}</h4>
                    {links.map((link) => {
                      const { title: linkTitle, url, target } = extractLink(link);

                      if (!url) {
                        return null;
                      }

                      const footerLinkTrackingId = buildTrackingId('footer-link', linkTitle);

                      return (
                        <li key={`${url}-${i}`}>
                          <Link
                            className="text-md text-neutral-40 hover:underline"
                            href={url.startsWith('mailto:') ? url : localizedLink(url)}
                            target={target}
                            onClick={getWithdrawalLinkClickHandler(url)}
                            data-tile-element-id={footerLinkTrackingId}
                          >
                            {linkTitle}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                );
              })}
            </div>
            {/* Mobile */}
            <div className=" flex flex-col border-b border-neutral-80 sm:hidden">
              {columnLinks?.map((column, i) => {
                const title = extractComponentFromChunk(column, 'title', 'SingleLineContent')?.text;
                const linksPieceContent = extractComponentFromChunk(
                  column,
                  'footer-column-link',
                  'PieceContent',
                )?.components?.[0]?.content;
                const links =
                  linksPieceContent?.__typename === 'ContentChunkContent'
                    ? linksPieceContent.chunks
                    : [];

                const columnToggleTrackingId = buildTrackingId('footer-mobile', title, 'toggle');

                return (
                  <Disclosure key={slugify(`${title}-${i}`)}>
                    {({ open }) => (
                      <>
                        <Disclosure.Button
                          className="flex items-center justify-between border-t border-neutral-80 py-3 text-lg font-medium"
                          data-tile-element-id={columnToggleTrackingId}
                        >
                          {title}
                          <PlusIcon
                            className={twJoin(
                              'h-8 w-auto text-primary-100 duration-200 ease-in',
                              open && 'rotate-45',
                            )}
                          />
                        </Disclosure.Button>
                        <Disclosure.Panel as="ul" className="flex list-none flex-col gap-2 pb-3">
                          {links.map((link, j) => {
                            const { title: linkTitle, url, target } = extractLink(link);

                            if (!url) {
                              return null;
                            }

                            const footerLinkTrackingId = buildTrackingId(
                              'footer-mobile-link',
                              linkTitle,
                            );

                            return (
                              <li key={slugify(`${url}-${j}`)}>
                                <Link
                                  className="text-md text-neutral-40 hover:underline"
                                  href={url.startsWith('mailto:') ? url : localizedLink(url)}
                                  target={target}
                                  onClick={getWithdrawalLinkClickHandler(url)}
                                  data-tile-element-id={footerLinkTrackingId}
                                >
                                  {linkTitle}
                                </Link>
                              </li>
                            );
                          })}
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                );
              })}
            </div>

            {hasSocials && (
              <div>
                <h4 className="mb-2 hidden font-medium sm:mb-4 md:block">Follow Us</h4>
                <div className="flex gap-4">
                  {socials?.map((social, i) => {
                    const icon = extractComponentFromChunk(social, 'icon', 'ImageContent');
                    const link = extractComponentFromChunk(
                      social,
                      'link',
                      'SingleLineContent',
                    )?.text;
                    const altText = icon?.images?.[0]?.altText;
                    const socialLinkTrackingId = buildTrackingId('footer-social', altText);

                    return (
                      <Link
                        key={slugify(`${link}-${i}`)}
                        href={link || '/'}
                        aria-label={altText || ''}
                        data-tile-element-id={socialLinkTrackingId}
                      >
                        <OptimizedImage
                          imageComponent={icon}
                          width={24}
                          height={24}
                          sizes="24px"
                          className="h-6 w-auto"
                        />
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Below xl this is a simple wrapping row. From xl up — the first width where four
                columns fit without squeezing — it adopts the link columns' tracks, so the badge
                sits under the second-to-last column and the selector under the last one. Negative
                line numbers keep that true for however many columns the CMS authors. */}
            <div
              className="flex flex-wrap items-end justify-between gap-8 xl:grid xl:gap-16"
              style={footerColumnsStyle}
            >
              {hasPaymentMethods && (
                <div>
                  <h4 className="mb-2 hidden font-medium sm:mb-4 md:block">Payment Methods</h4>
                  <div className="flex gap-4">
                    {paymentMethods?.map((paymentMethod) => {
                      const icon = extractComponentFromChunk(paymentMethod, 'icon', 'ImageContent');
                      return icon?.images?.map((img) => (
                        <OptimizedImage
                          key={slugify(img.url)}
                          image={img}
                          width={24}
                          height={24}
                          sizes="24px"
                          className="h-6 w-auto"
                        />
                      ));
                    })}
                  </div>
                </div>
              )}
              {/* w-full gives the badge its own flex line below xl, so the country selector keeps the
                  full width it had before and is not squeezed against the right edge. */}
              <LegalGuaranteeBadge className="w-full xl:col-start-[-3] xl:w-auto" />
              <div className="xl:col-start-[-2]">
                <LanguageCountrySelector
                  languageCountryList={languageCountryList}
                  chooserUiLanguage={chooserUiLanguage}
                  chooserUiMessages={chooserUiMessages}
                />
              </div>
            </div>
          </div>
          {/* BottomSection */}
          <div className="mt-14 flex flex-wrap justify-between gap-4">
            {/* Copyright */}
            <span>© {new Date().getFullYear()} Nuki Home Solutions, All Rights Reserved</span>
            <div className="flex flex-wrap items-center gap-4">
              {bottomLinks?.map((link, i) => {
                const title = extractComponentFromChunk(link, 'title', 'SingleLineContent')?.text;
                const internalLink = extractComponentFromChunk(
                  link,
                  'internal-link',
                  'ItemRelationsContent',
                )?.items?.[0]?.path;
                const externalLink = extractComponentFromChunk(
                  link,
                  'external-link',
                  'SingleLineContent',
                )?.text;
                const hasLink = internalLink || externalLink;

                if (!hasLink) {
                  return null;
                }

                const bottomLinkTrackingId = buildTrackingId('footer-bottom', title);

                return (
                  <Link
                    href={hasLink.startsWith('mailto:') ? hasLink : localizedLink(hasLink)}
                    className="block whitespace-nowrap text-sm text-neutral-40 underline duration-200 ease-in hover:text-white"
                    key={slugify(`${title}-${i}`)}
                    onClick={getWithdrawalLinkClickHandler(hasLink)}
                    data-tile-element-id={bottomLinkTrackingId}
                  >
                    {title}
                  </Link>
                );
              })}

              {country !== 'us' && <CookieManager />}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
